/**
 * Walk a question group's next_questions graph (mirrors backend form_branch.walk_branch_path).
 *
 * Display visibility for single-page forms uses ``visibleFormQuestions``: always-on-path
 * questions show up front; true conditionals appear once their branch is chosen.
 * Submit / completion still use ``walkBranchPath`` (the concrete answered path).
 */

const TERMINAL = new Set(['complete', 'ineligible']);

function normalizeNextQuestions(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const map = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (key === 'null') {
      map.null = value;
    } else if (key === 'true') {
      map.true = value;
    } else if (key === 'false') {
      map.false = value;
    } else {
      map[key] = value;
    }
  });
  return map;
}

function lookupNextValue(nextQuestions, answerValue) {
  if (!nextQuestions) {
    return { found: false, nextValue: null };
  }

  const tryKey = (key) => {
    if (key in nextQuestions) {
      return { found: true, nextValue: nextQuestions[key] };
    }
    return null;
  };

  let hit = tryKey(answerValue);
  if (hit) return hit;

  if (typeof answerValue === 'boolean') {
    hit = tryKey(answerValue ? 'Yes' : 'No');
    if (hit) return hit;
    hit = tryKey(String(answerValue).toLowerCase());
    if (hit) return hit;
  }

  const coerced = String(answerValue);
  hit = tryKey(coerced);
  if (hit) return hit;

  hit = tryKey(null);
  if (hit) return hit;

  hit = tryKey('null');
  if (hit) return hit;

  hit = tryKey('any');
  if (hit) return hit;

  return { found: false, nextValue: null };
}

function questionByOrder(questions, order) {
  const numeric = Number(order);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return questions.find((q) => q.order === numeric) || null;
}

function questionById(questions, id) {
  const numeric = Number(id);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return questions.find((q) => q.id === numeric) || null;
}

/**
 * Resolve next_questions for a single question within one group.
 * Cross-group targets are treated as terminal (not in `questions`).
 */
export function resolveNextTarget(question, answerValue, questions) {
  const nextQuestions = normalizeNextQuestions(question?.next_questions);
  if (!nextQuestions) {
    return { type: 'complete', targetQuestion: null };
  }

  const { found, nextValue } = lookupNextValue(nextQuestions, answerValue);
  if (!found) {
    return { type: 'complete', targetQuestion: null };
  }

  if (nextValue === -1) {
    return { type: 'complete', targetQuestion: null };
  }
  if (nextValue === null) {
    return { type: 'ineligible', targetQuestion: null };
  }

  if (typeof nextValue === 'string' && nextValue.includes(':')) {
    return { type: 'cross_group', targetQuestion: null };
  }

  if (typeof nextValue === 'number' && Number.isInteger(nextValue)) {
    const byOrder = questionByOrder(questions, nextValue);
    if (byOrder) {
      return { type: 'order', targetQuestion: byOrder };
    }
    const byId = questionById(questions, nextValue);
    if (byId) {
      return { type: 'question_id', targetQuestion: byId };
    }
    return { type: 'complete', targetQuestion: null };
  }

  const asInt = Number(nextValue);
  if (Number.isFinite(asInt)) {
    const byId = questionById(questions, asInt);
    if (byId) {
      return { type: 'question_id', targetQuestion: byId };
    }
    const byOrder = questionByOrder(questions, asInt);
    if (byOrder) {
      return { type: 'order', targetQuestion: byOrder };
    }
  }

  return { type: 'complete', targetQuestion: null };
}

function isAnswerPresent(question, value) {
  if (question.question_type === 'rich_content_only') {
    return true;
  }
  if (value === null || value === undefined) {
    return false;
  }
  if (question.question_type === 'address') {
    if (typeof value !== 'object') {
      return false;
    }
    return Boolean(
      String(value.address_1 || '').trim()
      && String(value.city || '').trim()
      && String(value.state || '').trim()
      && String(value.zip_code || '').length === 5,
    );
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return true;
}

/**
 * Optional questions may be left blank when the graph has an explicit
 * ``any`` / ``null`` continuation (or no next_questions at all). Branching
 * optionals without those keys still need an answer to choose a path.
 */
function canSkipUnanswered(question) {
  if (question?.required !== false) {
    return false;
  }
  const nextQuestions = normalizeNextQuestions(question?.next_questions);
  if (!nextQuestions || Object.keys(nextQuestions).length === 0) {
    return true;
  }
  return lookupNextValue(nextQuestions, undefined).found;
}

function resolveAnswerForWalk(question, answersByTag) {
  const tag = (question.semantic_tag || '').trim();
  let answer = tag ? answersByTag[tag] : undefined;
  if (question.question_type === 'rich_content_only') {
    return { answer: true, stop: false };
  }
  if (isAnswerPresent(question, answer)) {
    return { answer, stop: false };
  }
  if (canSkipUnanswered(question)) {
    return { answer: undefined, stop: false };
  }
  return { answer, stop: true };
}

/**
 * @param {Array} questions Active questions in order (first is entry)
 * @param {Object} answersByTag semantic_tag -> value
 * @returns {Array} questions on the walked path
 */
export function walkBranchPath(questions, answersByTag) {
  const list = Array.isArray(questions) ? questions : [];
  if (list.length === 0) {
    return [];
  }

  const byId = new Map(list.map((q) => [q.id, q]));
  const path = [];
  let current = list[0];
  const seen = new Set();

  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.push(current);

    const { answer, stop } = resolveAnswerForWalk(current, answersByTag);
    if (stop) {
      break;
    }

    const resolved = resolveNextTarget(current, answer, list);
    if (TERMINAL.has(resolved.type)) {
      break;
    }

    const target = resolved.targetQuestion;
    if (!target || !byId.has(target.id)) {
      break;
    }
    current = byId.get(target.id);
  }

  return path;
}

function syntheticAnswerForNextKey(key) {
  if (key === true || key === 'true' || key === 'Yes') return true;
  if (key === false || key === 'false' || key === 'No') return false;
  if (key === null || key === 'null' || key === 'any') return '__any__';
  return key;
}

/**
 * Unique outgoing branch targets for a question (deduped by destination).
 */
function uniqueBranchChoices(question, questions) {
  const nextQuestions = normalizeNextQuestions(question?.next_questions);
  if (!nextQuestions) {
    return [{ answer: true, resolved: { type: 'complete', targetQuestion: null } }];
  }

  const byDest = new Map();
  Object.keys(nextQuestions).forEach((key) => {
    const answer = syntheticAnswerForNextKey(key === 'null' ? null : key);
    const resolved = resolveNextTarget(question, answer, questions);
    let destKey = resolved.type;
    if (resolved.targetQuestion) {
      destKey = `q:${resolved.targetQuestion.id}`;
    } else if (TERMINAL.has(resolved.type)) {
      destKey = resolved.type;
    }
    if (!byDest.has(destKey)) {
      byDest.set(destKey, { answer, resolved });
    }
  });
  return Array.from(byDest.values());
}

/**
 * Every root→terminal path through the graph (for always-visible intersection).
 */
export function enumerateCompletePaths(questions) {
  const list = Array.isArray(questions) ? questions : [];
  if (list.length === 0) return [];

  const byId = new Map(list.map((q) => [q.id, q]));
  const paths = [];

  const walk = (current, path, seen) => {
    if (!current || seen.has(current.id)) {
      if (path.length) paths.push(path);
      return;
    }
    const nextPath = path.concat([current]);
    const nextSeen = new Set(seen);
    nextSeen.add(current.id);

    if (current.question_type === 'rich_content_only') {
      const resolved = resolveNextTarget(current, true, list);
      if (
        TERMINAL.has(resolved.type)
        || !resolved.targetQuestion
        || !byId.has(resolved.targetQuestion.id)
      ) {
        paths.push(nextPath);
        return;
      }
      walk(byId.get(resolved.targetQuestion.id), nextPath, nextSeen);
      return;
    }

    const choices = uniqueBranchChoices(current, list);
    if (choices.length === 0) {
      paths.push(nextPath);
      return;
    }

    choices.forEach(({ resolved }) => {
      if (
        TERMINAL.has(resolved.type)
        || !resolved.targetQuestion
        || !byId.has(resolved.targetQuestion.id)
      ) {
        paths.push(nextPath);
        return;
      }
      walk(byId.get(resolved.targetQuestion.id), nextPath, nextSeen);
    });
  };

  walk(list[0], [], new Set());
  return paths;
}

/**
 * Questions present on every complete path (always show on the single-page form).
 */
export function alwaysVisibleQuestions(questions) {
  const list = Array.isArray(questions) ? questions : [];
  const paths = enumerateCompletePaths(list);
  if (paths.length === 0) return list.slice(0, 1);

  let intersection = new Set(paths[0].map((q) => q.id));
  paths.slice(1).forEach((path) => {
    const ids = new Set(path.map((q) => q.id));
    intersection = new Set([...intersection].filter((id) => ids.has(id)));
  });

  return list.filter((q) => intersection.has(q.id));
}

/**
 * Single-page visibility: always-on-path questions + conditionals opened by answers.
 *
 * ``walkBranchPath`` stops at the first unanswered field, so a later branch answer
 * (e.g. same=No while shipping is empty) would never reveal visit. Here we also
 * open each answered question's concrete next target and soft-walk from there.
 */
export function visibleFormQuestions(questions, answersByTag) {
  const list = Array.isArray(questions) ? questions : [];
  if (list.length === 0) return [];

  const byId = new Map(list.map((q) => [q.id, q]));
  const visibleIds = new Set(alwaysVisibleQuestions(list).map((q) => q.id));

  const revealFrom = (start) => {
    let current = start;
    const seen = new Set();
    while (current && byId.has(current.id) && !seen.has(current.id)) {
      seen.add(current.id);
      visibleIds.add(current.id);

      const { answer, stop } = resolveAnswerForWalk(current, answersByTag);
      if (stop) {
        break;
      }

      const resolved = resolveNextTarget(current, answer, list);
      if (TERMINAL.has(resolved.type) || !resolved.targetQuestion) {
        break;
      }
      current = byId.get(resolved.targetQuestion.id);
    }
  };

  walkBranchPath(list, answersByTag).forEach((q) => visibleIds.add(q.id));

  list.forEach((q) => {
    const tag = (q.semantic_tag || '').trim();
    if (!tag) return;
    const answer = answersByTag[tag];
    if (answer === undefined || answer === null) return;

    const resolved = resolveNextTarget(q, answer, list);
    if (resolved.targetQuestion && byId.has(resolved.targetQuestion.id)) {
      revealFrom(resolved.targetQuestion);
    }
  });

  return list.filter((q) => visibleIds.has(q.id));
}

export function isPathComplete(questions, answersByTag) {
  const path = walkBranchPath(questions, answersByTag);
  if (path.length === 0) {
    return false;
  }

  for (let i = 0; i < path.length; i += 1) {
    const question = path[i];
    const tag = (question.semantic_tag || '').trim();
    const answer = tag ? answersByTag[tag] : undefined;
    if (question.question_type === 'rich_content_only') {
      continue;
    }
    if (isAnswerPresent(question, answer)) {
      continue;
    }
    if (canSkipUnanswered(question)) {
      continue;
    }
    return false;
  }

  const last = path[path.length - 1];
  const { answer } = resolveAnswerForWalk(last, answersByTag);
  const resolved = resolveNextTarget(last, answer, questions);
  return resolved.type === 'complete' || resolved.type === 'ineligible';
}

export function buildValuesForSubmit(questions, draftValues) {
  const path = walkBranchPath(questions, draftValues);
  const out = {};
  path.forEach((q) => {
    const tag = (q.semantic_tag || '').trim();
    if (!tag || q.question_type === 'rich_content_only') {
      return;
    }
    if (draftValues[tag] !== undefined) {
      out[tag] = draftValues[tag];
    }
  });
  return out;
}
